import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useGuide } from './GuideContext';

const PAD = 6;            // spotlight padding around the target
const GAP = 12;           // gap between target and tooltip
const TOOLTIP_W = 320;

interface Rect { top: number; left: number; width: number; height: number; }

/**
 * Spotlight + tooltip for the active guide step. Sits above modals (z-60),
 * never blocks clicks (the dimmed layers are pointer-events-none) so the
 * person does the real action themselves while the guide points at it.
 */
const GuideOverlay: React.FC = () => {
  const { guide, step, stepIndex, next, back, stop, isActive } = useGuide();
  const [rect, setRect] = useState<Rect | null>(null);
  const [tipHeight, setTipHeight] = useState(0);
  const tipRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<Element | null>(null);

  const selector = step ? `[data-guide="${step.target}"]` : '';

  // Track the target: poll until it exists (modals open later), then follow
  // it through scroll/resize. Re-run when the step changes.
  const measure = useCallback(() => {
    if (!selector) return;
    // First *visible* match: the sidebar exists twice (desktop + mobile drawer) and
    // a day can have many availability blocks — never spotlight a hidden one.
    const matches = Array.from(document.querySelectorAll(selector));
    const el = matches.find((n) => n.getClientRects().length > 0) || matches[0] || null;
    targetRef.current = el;
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    const nextRect = { top: r.top, left: r.left, width: r.width, height: r.height };
    // Only re-render when the box actually moved (this runs on a 250ms poll).
    setRect(prev => (
      prev && prev.top === nextRect.top && prev.left === nextRect.left
        && prev.width === nextRect.width && prev.height === nextRect.height ? prev : nextRect
    ));
  }, [selector]);

  useEffect(() => {
    if (!isActive) return;
    measure();
    const poll = window.setInterval(measure, 250);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [isActive, measure]);

  // Scroll the target into view when a step (re)targets.
  useEffect(() => {
    if (!isActive || !rect) return;
    const el = targetRef.current;
    if (el && (rect.top < 0 || rect.top + rect.height > window.innerHeight)) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    // only when the step changes or the target first appears
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, stepIndex, rect === null]);

  // advanceOn: 'click' — listen on the document so the real handler still runs.
  useEffect(() => {
    if (!isActive || !step || step.advanceOn !== 'click') return;
    const onClick = (e: MouseEvent) => {
      // Any element carrying this step's anchor counts (e.g. whichever green
      // block the person clicks), not just the one under the spotlight.
      if (e.target instanceof Element && e.target.closest(selector)) {
        // Let the element's own handler run first (opens modal / navigates).
        window.setTimeout(next, 50);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isActive, step, selector, next]);

  // Escape exits the guide.
  useEffect(() => {
    if (!isActive) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') stop(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isActive, stop]);

  // Re-measure the tooltip whenever its content or anchor changes so it can
  // flip above/below the target without overflowing the viewport.
  useLayoutEffect(() => {
    if (tipRef.current) setTipHeight(tipRef.current.offsetHeight);
  }, [step, rect]);

  if (!isActive || !guide || !step) return null;

  const total = guide.steps.length;
  const isLast = stepIndex >= total - 1;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // --- tooltip position ---------------------------------------------------
  let tipStyle: React.CSSProperties;
  if (rect) {
    const below = rect.top + rect.height + GAP + tipHeight < vh - 8;
    const wantTop = step.placement === 'top' && rect.top - GAP - tipHeight > 8;
    const placeBelow = wantTop ? false : below || rect.top - GAP - tipHeight < 8;
    const top = placeBelow ? rect.top + rect.height + GAP : rect.top - GAP - tipHeight;
    const left = Math.min(Math.max(8, rect.left + rect.width / 2 - TOOLTIP_W / 2), vw - TOOLTIP_W - 8);
    tipStyle = { position: 'fixed', top, left, width: Math.min(TOOLTIP_W, vw - 16) };
  } else {
    tipStyle = { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', width: Math.min(TOOLTIP_W, vw - 16) };
  }

  const spot = rect ? {
    top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2,
  } : null;

  return createPortal(
    <div className="fixed inset-0 z-[60] pointer-events-none" aria-live="polite" data-testid="guide-overlay">
      {/* Dim everything except the spotlight (four panels around it). */}
      {spot ? (
        <>
          <div className="absolute bg-clinic-dark/45" style={{ top: 0, left: 0, right: 0, height: Math.max(0, spot.top) }} />
          <div className="absolute bg-clinic-dark/45" style={{ top: spot.top + spot.height, left: 0, right: 0, bottom: 0 }} />
          <div className="absolute bg-clinic-dark/45" style={{ top: spot.top, left: 0, width: Math.max(0, spot.left), height: spot.height }} />
          <div className="absolute bg-clinic-dark/45" style={{ top: spot.top, left: spot.left + spot.width, right: 0, height: spot.height }} />
          <div
            className="absolute rounded-lg ring-4 ring-clinic-gold/80 transition-all duration-200"
            style={{ top: spot.top, left: spot.left, width: spot.width, height: spot.height }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-clinic-dark/20" />
      )}

      {/* Tooltip */}
      <div
        ref={tipRef}
        role="dialog"
        aria-label={`Guide: ${guide.title}`}
        className="pointer-events-auto bg-white rounded-2xl shadow-clinic-lg border border-clinic-primary-lighter p-4 animate-fadeIn"
        style={tipStyle}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-clinic-muted">{guide.title} · {stepIndex + 1} of {total}</p>
            <h3 className="text-base font-semibold text-clinic-dark mt-0.5">{step.title}</h3>
          </div>
          <button
            type="button"
            onClick={stop}
            aria-label="Exit guide"
            className="p-1 -m-1 text-clinic-muted hover:text-clinic-dark rounded-lg"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="text-sm text-clinic-dark mt-2">{step.body}</p>
        {!rect && (
          <p className="text-xs text-clinic-muted mt-2">Waiting for that part of the page to appear…</p>
        )}
        {rect && step.advanceOn === 'click' && (
          <p className="text-xs text-clinic-gold mt-2">
            {isLast ? 'Click the highlighted button to finish. This does the real thing.' : 'Click the highlighted item to continue.'}
          </p>
        )}
        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={back}
            disabled={stepIndex === 0}
            className="text-sm text-clinic-muted hover:text-clinic-dark disabled:opacity-40"
          >
            Back
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={stop} className="text-sm text-clinic-muted hover:text-clinic-dark">
              Exit
            </button>
            {/* "Click this" steps have no Next: the action itself moves the guide on,
                so the guide can never get ahead of what the person actually did. */}
            {step.advanceOn !== 'click' && (
              <button
                type="button"
                onClick={next}
                className="px-3 py-1.5 text-sm font-medium rounded-lg bg-clinic-primary text-clinic-cream hover:bg-clinic-primary-dark"
              >
                {isLast ? 'Done' : 'Next'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GuideOverlay;
